import React, { useEffect, useRef, useState } from "react";
import api from "../api/client";
import ActionButton from "./ActionButton";

/**
 * One-click Microsoft 365 tenant connect. Replaces the old manual "paste your
 * tenant/client/secret" flow: the operator downloads a signed one-time tool,
 * runs it once as a Global Admin of the customer's tenant, and it registers the
 * app + grants admin consent + posts the connection back to the RMM. This modal
 * polls until that post-back lands. Used by both the M365 dashboard and the
 * Tenants page. Scoped to the customer chosen in the global switcher — no
 * customer picker here.
 */
export default function M365AutoConnectModal({
  customerId,
  customerName,
  onClose,
  onDone,
}: {
  customerId: number;
  customerName?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<"ready" | "waiting" | "done">("ready");
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const poll = async () => {
    const token = tokenRef.current;
    if (!token) return;
    try {
      const r = await api.get("/api/v1/m365/tenants/setup/status", { params: { token } });
      if (r.data.status === "done") {
        setPhase("done");
        onDone();
        return;
      }
    } catch {
      /* transient — keep polling */
    }
    timerRef.current = setTimeout(poll, 4000);
  };

  const start = async () => {
    setError(null);
    try {
      const r = await api.post("/api/v1/m365/tenants/setup/start", { customer_id: customerId });
      tokenRef.current = r.data.token;
      const bytes = Uint8Array.from(atob(r.data.script_b64), (ch) => ch.charCodeAt(0));
      const url = window.URL.createObjectURL(
        new Blob([bytes], { type: "application/octet-stream" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = r.data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setPhase("waiting");
      timerRef.current = setTimeout(poll, 4000);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Could not start setup");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-5 w-full max-w-lg space-y-3">
        <h3 className="text-lg font-semibold">Connect Microsoft 365</h3>
        {phase === "ready" && (
          <p className="text-sm text-gray-600">
            Connecting{" "}
            <span className="font-medium text-gray-900">{customerName || "this customer"}</span>.
            Download the one-click tool, then run it once signed in as a Global Admin of that
            customer's tenant. It registers the app, grants admin consent, and sends the connection
            back here automatically — nothing to copy or paste, no files to edit.
          </p>
        )}
        {phase === "waiting" && (
          <div className="text-sm text-gray-700 py-4">
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full bg-emerald-500 animate-pulse" />
              Waiting for the tool to finish in the customer's tenant…
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Run <span className="font-mono">AeroGauge-M365-Setup.cmd</span> (just downloaded) as a
              Global Admin. This window updates automatically when it reports back — keep it open.
              The setup link expires in 30 minutes.
            </p>
          </div>
        )}
        {phase === "done" && (
          <div className="text-sm text-emerald-700 py-4 flex items-center gap-2">
            <span>✓</span> Tenant connected. Syncing will begin shortly.
          </div>
        )}
        {error && <div className="text-sm bg-red-50 text-red-800 p-2 rounded">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50">
            {phase === "done" ? "Close" : "Cancel"}
          </button>
          {phase === "ready" && (
            <ActionButton onAction={() => start()} variant="raw" className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium" pendingLabel="Working…">
              Download &amp; connect
            </ActionButton>
          )}
        </div>
      </div>
    </div>
  );
}
